import type { IHaulierSelectItem } from "@/customTypings/haulier/Haulier";
import type { IVehicleSelectItem } from "@/customTypings/vehicle/Vehicle";
import type {
    IRegEwcLookupItem,
    IWasteMovementDetail,
    WasteMovementCarrierPayload,
    WasteMovementDirection,
} from "@/customTypings/wasteMovement/WasteMovement";
import {
    WASTE_MOVEMENT_RECEIPT_CAPTURE_FIELDS,
    type WasteMovementFormValues,
    type WasteMovementReceiptCaptureFormValues,
    type WasteMovementTriState,
} from "../model/formValues";
import { combineReceiptDateTime, splitReceiptDateTime } from "./wasteMovementReceiptTime";
import { hasAnyHazardousPopsValue } from "./wasteMovementHazardousPops";
import { hasAnyBrokerDealerValue } from "./wasteMovementBrokerDealer";

/** Regulatory vocabularies mirrored from the backend constants (case-sensitive). */
export const MEANS_OF_TRANSPORT_ROAD = "Road";
export const MEANS_OF_TRANSPORT_OPTIONS: readonly string[] = ["Road", "Rail", "Air", "Sea", "Inland Waterway", "Piped", "Other"];
export const NO_REGISTRATION_REASON_OPTIONS: readonly { code: string; label: string }[] = [
    { code: "ON_SITE", label: "Movement within the same premises" },
    { code: "HOUSEHOLD", label: "Householder transporting their own waste" },
    { code: "ONE_OFF", label: "One-off or infrequent waste transport" },
    { code: "MARINE", label: "Marine transport under a marine licence or exemption" },
];

export type ReceiptCaptureSnapshot = Pick<
    IWasteMovementDetail,
    | "receivedAtUtc"
    | "wasteDescription"
    | "containerTypeCode"
    | "numberOfContainers"
    | "weightIsEstimate"
    | "containsHazardous"
    | "containsPops"
    | "carrier"
    | "brokerDealer"
>;

/** A saved record that pre-dates receipt capture: nothing captured, no carrier. Its capture stays optional-as-a-whole. */
export function isLegacyRecordWithoutReceiptCapture(detail: ReceiptCaptureSnapshot | null | undefined): boolean {
    if (!detail) return false;
    return (
        !detail.receivedAtUtc &&
        !detail.wasteDescription &&
        !detail.containerTypeCode &&
        detail.numberOfContainers == null &&
        detail.weightIsEstimate == null &&
        detail.containsHazardous == null &&
        detail.containsPops == null &&
        !detail.carrier &&
        !detail.brokerDealer
    );
}

export function hasAnyReceiptCaptureValue(values: WasteMovementFormValues): boolean {
    const s1b = WASTE_MOVEMENT_RECEIPT_CAPTURE_FIELDS.some((field) => {
        if (field === "carrierMeansOfTransport") return false; // defaulted to Road; not an operator signal on its own
        if (field === "receivedTimeOccurrence") return false; // only meaningful alongside a time; never a signal on its own
        return (values[field] ?? "").toString().trim() !== "";
    });
    return s1b || hasAnyHazardousPopsValue(values) || hasAnyBrokerDealerValue(values);
}

/**
 * Receipt capture is required on every new Received movement, on every record that was already captured,
 * and on a legacy record as soon as the operator starts filling it in.
 */
export function isReceiptCaptureRequired(args: {
    movementDirection: WasteMovementDirection;
    isNew: boolean;
    detail: ReceiptCaptureSnapshot | null | undefined;
    values: WasteMovementFormValues;
}): boolean {
    if (args.movementDirection !== "Received") return false;
    if (args.isNew) return true;
    if (!isLegacyRecordWithoutReceiptCapture(args.detail)) return true;
    return hasAnyReceiptCaptureValue(args.values);
}

export function triStateFromBoolean(value: boolean | null | undefined): WasteMovementTriState {
    if (value === true) return "true";
    if (value === false) return "false";
    return "";
}

export function booleanFromTriState(value: WasteMovementTriState | string): boolean | null {
    if (value === "true") return true;
    if (value === "false") return false;
    return null;
}

/** Receipt date/time conversions live in wasteMovementReceiptTime (Europe/London site time, never the browser zone). */
export { combineReceiptDateTime, splitReceiptDateTime } from "./wasteMovementReceiptTime";

/** Selecting a haulier copies its name and licence onto the movement; the copy is what gets saved. */
export function applyHaulierPrefill(
    values: WasteMovementFormValues,
    haulier: IHaulierSelectItem | null
): WasteMovementFormValues {
    if (!haulier) return { ...values, carrierHaulierId: "" };
    const licence = (haulier.carrierLicenceNumber ?? "").trim();
    return {
        ...values,
        carrierHaulierId: haulier.id,
        carrierOrganisationName: haulier.name,
        carrierRegistrationNumber: licence,
        carrierReasonForNoRegistrationNumber: licence ? "" : values.carrierReasonForNoRegistrationNumber,
    };
}

/** Selecting a vehicle copies its registration onto the movement; the copy is what gets saved. */
export function applyVehiclePrefill(
    values: WasteMovementFormValues,
    vehicle: IVehicleSelectItem | null
): WasteMovementFormValues {
    if (!vehicle) return { ...values, carrierVehicleId: "" };
    return { ...values, carrierVehicleId: vehicle.id, carrierVehicleRegistration: vehicle.registration };
}

/** Default the hazardous flag from the EWC's reference classification only while the operator has not chosen. */
export function defaultContainsHazardousFromEwc(
    values: WasteMovementFormValues,
    ewc: IRegEwcLookupItem | null | undefined
): WasteMovementFormValues {
    if (!ewc || typeof ewc.isHazardous !== "boolean") return values;
    if (values.containsHazardous !== "") return values;
    return { ...values, containsHazardous: triStateFromBoolean(ewc.isHazardous) };
}

export function receiptCaptureFormValuesFromDetail(
    detail: ReceiptCaptureSnapshot | null | undefined
): WasteMovementReceiptCaptureFormValues {
    const { time, occurrence } = splitReceiptDateTime(detail?.receivedAtUtc);
    const carrier = detail?.carrier;
    return {
        receivedTime: time,
        receivedTimeOccurrence: occurrence,
        wasteDescription: detail?.wasteDescription ?? "",
        containerTypeCode: detail?.containerTypeCode ?? "",
        numberOfContainers: detail?.numberOfContainers == null ? "" : String(detail.numberOfContainers),
        weightIsEstimate: triStateFromBoolean(detail?.weightIsEstimate),
        containsHazardous: triStateFromBoolean(detail?.containsHazardous),
        containsPops: triStateFromBoolean(detail?.containsPops),
        carrierHaulierId: carrier?.haulierId ?? "",
        carrierOrganisationName: carrier?.organisationName ?? "",
        carrierRegistrationNumber: carrier?.registrationNumber ?? "",
        carrierReasonForNoRegistrationNumber: carrier?.reasonForNoRegistrationNumber ?? "",
        carrierMeansOfTransport: carrier?.meansOfTransport ?? (carrier ? "" : MEANS_OF_TRANSPORT_ROAD),
        carrierVehicleId: carrier?.vehicleId ?? "",
        carrierVehicleRegistration: carrier?.vehicleRegistration ?? "",
    };
}

export type ReceiptCapturePayload = {
    receivedAtUtc: string | null;
    wasteDescription: string | null;
    containerTypeCode: string | null;
    numberOfContainers: number | null;
    weightIsEstimate: boolean | null;
    containsHazardous: boolean | null;
    containsPops: boolean | null;
    carrier: WasteMovementCarrierPayload | null;
};

const EMPTY_RECEIPT_PAYLOAD: ReceiptCapturePayload = {
    receivedAtUtc: null,
    wasteDescription: null,
    containerTypeCode: null,
    numberOfContainers: null,
    weightIsEstimate: null,
    containsHazardous: null,
    containsPops: null,
    carrier: null,
};

function textOrNull(value: string): string | null {
    const t = (value ?? "").trim();
    return t === "" ? null : t;
}

/**
 * Receipt capture as sent to the API. Removed movements never send it. A Received movement that the operator has left
 * entirely unfilled (legacy record) sends nulls and no carrier, so nothing stored is touched.
 */
export function buildReceiptCapturePayload(
    values: WasteMovementFormValues,
    movementDirection: WasteMovementDirection
): ReceiptCapturePayload {
    if (movementDirection !== "Received") return { ...EMPTY_RECEIPT_PAYLOAD };
    if (!hasAnyReceiptCaptureValue(values)) return { ...EMPTY_RECEIPT_PAYLOAD };

    const count = values.numberOfContainers.trim();
    const carrierTouched =
        textOrNull(values.carrierOrganisationName) !== null ||
        textOrNull(values.carrierRegistrationNumber) !== null ||
        textOrNull(values.carrierReasonForNoRegistrationNumber) !== null ||
        textOrNull(values.carrierVehicleRegistration) !== null ||
        textOrNull(values.carrierHaulierId) !== null ||
        textOrNull(values.carrierVehicleId) !== null;

    return {
        receivedAtUtc: combineReceiptDateTime(values.movementDate, values.receivedTime, values.receivedTimeOccurrence),
        wasteDescription: textOrNull(values.wasteDescription),
        containerTypeCode: textOrNull(values.containerTypeCode),
        numberOfContainers: count === "" || Number.isNaN(Number(count)) ? null : Number(count),
        weightIsEstimate: booleanFromTriState(values.weightIsEstimate),
        containsHazardous: booleanFromTriState(values.containsHazardous),
        containsPops: booleanFromTriState(values.containsPops),
        carrier: carrierTouched
            ? {
                  haulierId: textOrNull(values.carrierHaulierId),
                  vehicleId: textOrNull(values.carrierVehicleId),
                  organisationName: textOrNull(values.carrierOrganisationName),
                  registrationNumber: textOrNull(values.carrierRegistrationNumber),
                  reasonForNoRegistrationNumber: textOrNull(values.carrierReasonForNoRegistrationNumber),
                  meansOfTransport: textOrNull(values.carrierMeansOfTransport),
                  vehicleRegistration: textOrNull(values.carrierVehicleRegistration),
              }
            : null,
    };
}
